// src/components/FileUpload.js

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../../supabase.js';
import toast from 'react-hot-toast';
import { Spinner } from './Spinner';
import confetti from 'canvas-confetti';
import Modal from 'react-modal';
import '../modals/SummaryModal.css';

Modal.setAppElement('#root');

export const FileUpload = ({ onUploadSuccess, user }) => {
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setFileToUpload(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isUploading
  });

  const handleFileUpload = async () => {
    if (!fileToUpload) {
      return toast.error("Please select a file first!");
    }
    setIsUploading(true);
    
    if (!user) {
      toast.error("You must be logged in to upload files.");
      setIsUploading(false);
      return;
    }
    
    try {
      // 1. Upload file to Supabase Storage
      const filePath = `${user.id}/${Date.now()}_${fileToUpload.name}`;
      const { error: storageError } = await supabase.storage
        .from('files')
        .upload(filePath, fileToUpload);

      if (storageError) throw storageError;

      // 2. Get Public URL
      const { data: publicUrlData } = supabase.storage
        .from('files')
        .getPublicUrl(filePath);

      const fileUrl = publicUrlData.publicUrl;

      // Real Auto-Categorization & Auto-Tagging!
      const activeName = fileToUpload.name.toLowerCase();
      const isAiSupported = activeName.endsWith('.pdf') || 
                            activeName.endsWith('.txt') || 
                            activeName.endsWith('.csv') || 
                            activeName.endsWith('.json') || 
                            activeName.endsWith('.md') || 
                            activeName.endsWith('.docx') || 
                            activeName.endsWith('.xlsx') || 
                            activeName.endsWith('.pptx') || 
                            !!activeName.match(/\.(png|jpe?g|gif|webp|svg)$/);

      let category = null;
      let tags = [];

      if (isAiSupported) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          // 1. Auto-Categorize
          try {
            const response = await fetch('/api/categorizeFile', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}` 
              },
              body: JSON.stringify({ data: { fileUrl: fileUrl, fileName: fileToUpload.name, currentTags: [] } }),
            });
            const result = await response.json();
            if (response.ok && result.data && result.data.category) {
              category = result.data.category;
            }
          } catch (catErr) {
            console.error("Auto-categorize error:", catErr);
          }

          // 2. Auto-Tagging
          try {
            const response = await fetch('/api/generateTags', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}` 
              },
              body: JSON.stringify({ data: { fileUrl: fileUrl, fileName: fileToUpload.name, limit: 5 } }),
            });
            const result = await response.json();
            if (response.ok && result.data && result.data.tags) {
              tags = result.data.tags;
            }
          } catch (tagErr) {
            console.error("Auto-tagging error:", tagErr);
          }
        } catch (autoErr) {
          console.error("Genie AI automatic processing failed:", autoErr);
        }
      }

      // 3. Insert finalized record with tags & category into Postgres table
      const { error: dbError } = await supabase
        .from('files')
        .insert([
          {
            uid: user.id,
            fileName: fileToUpload.name,
            fileUrl: fileUrl,
            size: fileToUpload.size,
            category: category || 'Other',
            tags: tags || []
          }
        ]);

      if (dbError) throw dbError;

      // Confetti burst!
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#2e5077', '#4bc0c0', '#36a2eb', '#fca5a5', '#ffffff']
      });

      toast.success(`"${fileToUpload.name}" uploaded successfully!`);
      
      // Notify App.js to refresh list
      if (onUploadSuccess) onUploadSuccess();

    } catch (err) {
      console.error("Error during file upload:", err);
      toast.error(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setFileToUpload(null);
      setIsUploading(false);
    }
  };

  return (
    <div className="upload-container">
      <div {...getRootProps({ className: `dropzone ${isDragActive ? 'active' : ''}` })}>
        <input {...getInputProps()} />
        { isDragActive ? <p>Drop the file here ...</p> :
          fileToUpload ? <p>Selected file: <strong>{fileToUpload.name}</strong></p> :
          <p>Drag & drop or click to upload</p> }
      </div>
      
      <Modal
        isOpen={isUploading}
        contentLabel="Uploading File Modal"
        className="modal summary-modal-content"
        overlayClassName="overlay"
        style={{ content: { maxWidth: '500px' } }}
        ariaHideApp={false}
      >
        <div className="summary-loading-state" style={{ padding: '20px 10px' }}>
          <p style={{ fontWeight: '600', fontSize: '16px', color: 'var(--color-text-primary)', marginBottom: '16px', textAlign: 'center' }}>
            Uploading...
          </p>
          <div className="summary-skeleton-wave" />
          <div className="summary-skeleton-wave" style={{ animationDelay: '0.2s', width: '85%' }} />
          <div className="summary-skeleton-wave" style={{ animationDelay: '0.4s', width: '90%' }} />
        </div>
      </Modal>

      <button onClick={handleFileUpload} disabled={isUploading || !fileToUpload} style={{marginTop: '15px'}}>
        {isUploading ? (<><Spinner /><span>Processing...</span></>) : ('Upload File')}
      </button>
    </div>
  );
};